import {
  FiltersContext,
  FiltersDispatchContext,
  useSealedParamsContext
} from '@/modules/accredited-establishments/contexts'

import { useContext, useEffect, useMemo, useRef } from 'react'
import {
  useGetSpecialtiesFilter,
  useGetAddressesFilter,
  useGetPlansFilter,
  useGetSubSpecialtiesFilter,
  useSealedSearchParams
} from '@/modules/accredited-establishments/hooks'

import { usePathname } from 'next/navigation'
import {
  ADDRESS_SEARCH_PARAMS,
  CATEGORIES_PARAMS,
  SPECIALTY_PARAMS,
  SUB_SPECIALTY_PARAMS
} from './useInitFilters.constants'
import { clearInvalidParams, getValidParams } from './useInitFilters.helpers'

/**
 * Syncs initial URL params with filters state to fetch inital filters options
 */
const useInitFilters = () => {
  const { ready: filtersReady } = useContext(FiltersContext)
  const filtersDispatch = useContext(FiltersDispatchContext)
  const { paramsRef } = useSealedParamsContext()
  const { updateSealedSearchParams } = useSealedSearchParams()

  const initiated = useRef(false)

  /* O registro ja chega decifrado do contexto; o hook inicializa uma unica vez. */
  const searchParams = useMemo(
    () => new URLSearchParams(paramsRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  const pathname = usePathname()

  const addressSearchLabel = useMemo(
    () => searchParams?.get(ADDRESS_SEARCH_PARAMS) || '',
    [searchParams]
  )

  /*
   * Restoring the subspecialty costs one request the other filters do not need,
   * so it is only made when the url can actually use the answer. The SPECIALIST
   * guard mirrors getValidParams: without that category the specialty itself is
   * discarded, and any subspecialty resolved here would go with it.
   */
  const specialtyToRestore = useMemo(() => {
    if (!searchParams?.get(SUB_SPECIALTY_PARAMS)) return undefined

    const isSpecialistSearch = searchParams
      .get(CATEGORIES_PARAMS)
      ?.split(',')
      .includes('SPECIALIST')

    return isSpecialistSearch
      ? searchParams.get(SPECIALTY_PARAMS) || undefined
      : undefined
  }, [searchParams])

  const specialties = useGetSpecialtiesFilter()
  const plans = useGetPlansFilter()
  const addresses = useGetAddressesFilter({ addressSearchLabel })
  const subSpecialties = useGetSubSpecialtiesFilter(specialtyToRestore)

  const addressIsSettled = addressSearchLabel ? addresses.isSuccess : true

  /*
   * `isFetched`, not `isSuccess`: a failed subspecialties request must still
   * let the page initialize — the param is then dropped as unresolvable.
   */
  const subSpecialtiesAreSettled = specialtyToRestore
    ? subSpecialties.isFetched
    : true

  const isSetttled = useMemo(
    () =>
      specialties.isSuccess &&
      plans.isSuccess &&
      addressIsSettled &&
      subSpecialtiesAreSettled,
    [
      specialties.isSuccess,
      plans.isSuccess,
      addressIsSettled,
      subSpecialtiesAreSettled
    ]
  )

  useEffect(() => {
    if (!isSetttled || !searchParams || filtersReady || initiated.current)
      return

    const { filters, params } = getValidParams({
      searchParams,
      specialties: specialties.data!,
      plans: plans.data!,
      addresses: addresses.data,
      subSpecialties: subSpecialties.data
    })

    filtersDispatch({
      type: 'INIT_FILTERS',
      payload: filters
    })

    initiated.current = true

    const keysToRemove = clearInvalidParams({
      searchParams,
      validCategories: params.categories,
      validSpecialty: params.specialty,
      validSubSpecialty: params.subSpecialty,
      validGender: params.gender,
      validAppointmentType: params.appointmentType,
      validPlan: params.plan,
      validAddress: params.address,
      validProviderName: params.providerName
    })

    if (keysToRemove.length) {
      updateSealedSearchParams({
        pathname,
        newParam: keysToRemove.map(key => [key, ''])
      })
    }
  }, [
    isSetttled,
    searchParams,
    pathname,
    specialties.data,
    plans.data,
    addresses.data,
    subSpecialties.data,
    filtersDispatch,
    filtersReady,
    updateSealedSearchParams
  ])
}

export { useInitFilters }
